import { z } from 'zod';
import { uuid } from './common.js';
import { deliveryOption, imageDto, shopStorefront, videoDto } from './shopfront.js';
import { storefrontCustomerDto } from './customers.js';
/**
 * The public storefront API (docs/design/10 P2.3). Texts come back already in the visitor's
 * language; prices come back as the visitor should see them (incl. or excl. VAT per shop).
 */
export const VAT_RATE = 0.25;
export const sfShopDto = z.object({
    id: uuid,
    slug: z.string(),
    name: z.string(),
    lang: z.enum(['da', 'en']),
    theme: shopStorefront.shape.theme,
    colors: z.object({ primary: z.string(), accent: z.string() }),
    logo: imageDto.nullable(),
    hero: imageDto.nullable(),
    languages: z.array(z.enum(['da', 'en'])),
    access: z.enum(['open', 'login', 'gift_code']),
    selfRegistration: z.boolean(),
    showPrices: z.boolean(),
    pricesInclVat: z.boolean(),
    giftGrouping: z.boolean(),
    declineAllowed: z.boolean(),
    currency: z.string(),
    /** open; not_open before opensAt; closed after closesAt. */
    state: z.enum(['open', 'not_open', 'closed']),
    opensAt: z.string().nullable(),
    closesAt: z.string().nullable(),
    welcome: z.string(),
    footer: z.string(),
    contactEmail: z.string(),
    contactPhone: z.string(),
    termsUrl: z.string(),
    seoIndex: z.boolean(),
    /** False until checkout (P2.4) is switched on for the shop. */
    checkoutReady: z.boolean(),
    /** A demo shop (docs/design/12 section 6): orders stay inside the system; the storefront shows a ribbon. */
    demo: z.boolean(),
    paymentMethods: z.array(z.enum(['bambora', 'invoice'])),
    deliveryOptions: z.array(deliveryOption),
    checkoutFields: z.record(z.string(), z.enum(['required', 'optional', 'hidden'])),
    customFields: z.array(z.object({ key: z.string(), label: z.string(), required: z.boolean() })),
    departments: z.array(z.string()),
    budgets: z.array(z.string()),
    approvalRequired: z.boolean(),
    requireTerms: z.boolean(),
    menu: z.array(z.object({ slug: z.string(), title: z.string() })),
    slides: z.array(z.object({ image: imageDto, title: z.string(), linkUrl: z.string().nullable() })),
    categories: z.array(z.object({ id: uuid, parentId: uuid.nullable(), slug: z.string(), name: z.string(), image: imageDto.nullable(), productCount: z.number().int() })),
});
export const sfContextDto = z.object({ shop: sfShopDto, customer: storefrontCustomerDto.nullable() });
export const sfProductCardDto = z.object({
    id: uuid,
    slug: z.string(),
    number: z.string().nullable(),
    title: z.string(),
    vendor: z.string().nullable(),
    vendorSlug: z.string().nullable(),
    image: imageDto.nullable(),
    /** The second gallery image, shown on hover. */
    hoverImage: imageDto.nullable(),
    /** Set when the product has one variant: it can go in the cart straight from the card. */
    variantId: uuid.nullable(),
    isNew: z.boolean(),
    imageUrl: z.string().nullable(),
    /** Lowest variant price as shown, null when the shop hides prices. */
    priceMinor: z.number().int().nullable(),
    compareAtMinor: z.number().int().nullable(),
    fromPrice: z.boolean(),
    groupName: z.string().nullable(),
    inStock: z.boolean(),
});
export const sfProductPageDto = z.object({ items: z.array(sfProductCardDto), total: z.number().int(), page: z.number().int(), perPage: z.number().int(), vendors: z.array(z.object({ slug: z.string(), name: z.string(), count: z.number().int() })) });
export const sfProductDto = sfProductCardDto.extend({
    description: z.string(),
    images: z.array(imageDto),
    videos: z.array(videoDto),
    tags: z.array(z.string()),
    categoryIds: z.array(uuid),
    variants: z.array(z.object({
        id: uuid,
        sku: z.string(),
        title: z.string(),
        options: z.record(z.string(), z.string()),
        priceMinor: z.number().int().nullable(),
        compareAtMinor: z.number().int().nullable(),
        /** A sampak: what one unit contains; `productSlug` when that product is sold in the shop too. */
        components: z.array(z.object({ sku: z.string(), title: z.string(), quantity: z.number().int(), productSlug: z.string().nullable() })),
        stepPrices: z.array(z.object({ minQty: z.number().int(), priceMinor: z.number().int() })),
        minQty: z.number().int(),
        maxQty: z.number().int().nullable(),
        stepQty: z.number().int(),
        allowedQty: z.array(z.number().int()),
        available: z.number().int(),
    })),
});
export const SF_SORTS = ['position', 'price_asc', 'price_desc', 'name', 'newest'];
export const sfProductQuery = z.object({
    category: z.string().max(120).optional(),
    q: z.string().trim().max(100).optional(),
    vendor: z.string().max(120).optional(),
    sort: z.enum(SF_SORTS).default('position'),
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(200).default(48),
});
export const sfPageDto = z.object({ slug: z.string(), title: z.string(), body: z.string() });
export const altchaChallengeDto = z.object({ algorithm: z.literal('SHA-256'), challenge: z.string(), maxnumber: z.number().int(), salt: z.string(), signature: z.string() });
export const sfContactRequest = z.object({
    name: z.string().trim().min(1).max(200),
    email: z.string().trim().toLowerCase().email().max(200),
    phone: z.string().trim().max(50).optional(),
    message: z.string().trim().min(1).max(5000),
    altcha: z.string().min(10).max(2000),
    website: z.string().max(0).optional(),
});
//# sourceMappingURL=storefront.js.map